import React, { useState } from "react";
import {
  Modal,
  Button,
  TextInput,
  PasswordInput,
  Group,
  MantineProvider,
  Divider,
  Paper,
  Text,
  Box,
  Stack,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import fetchApi from "../Lib/api";
import Notification from "../Lib/Notification";
import { GoogleButton } from "./GoogleButton";
import { LinkedInButton } from "./LinkedInButton";
import Mixpanel from "../Lib/mixpanel";
import {
  IconArrowRight,
  IconUser,
  IconMail,
  IconLock,
} from "@tabler/icons-react";

export default function SignUpModal({ token, tenant_name, tenant_logo }) {
  const [opened, setOpened] = useState(false);
  const [notification, setNotification] = useState(null);
  const [loading, setLoading] = useState(false);

  const form = useForm({
    initialValues: {
      first_name: "",
      last_name: "",
      email: "",
      password: "",
      password_confirmation: "",
    },
    validate: {
      first_name: (val) =>
        val.trim().length > 0 ? null : "First name is required",
      last_name: (val) =>
        val.trim().length > 0 ? null : "Last name is required",
      email: (val) => (/^\S+@\S+$/.test(val) ? null : "Invalid email"),
      password: (val) =>
        val.length >= 6
          ? null
          : "Password should include at least 6 characters",
      password_confirmation: (val, values) =>
        val === values.password ? null : "Password confirmation does not match",
    },
  });

  const effectiveToken = token || new URLSearchParams(window.location.search).get("token");

  const handleSocialLogin = async (provider) => {
    setNotification(null);
    setLoading(true);

    Mixpanel.track("User Signup Attempted", {
      signup_method: provider,
      tenant_name: tenant_name,
    });

    try {
      // Store tenant_token in session if provided
      if (effectiveToken) {
        await fetchApi('/users/auth/store_tenant_token', 'POST', { token: effectiveToken });
      }

      if (provider === 'linkedin') {
        // LinkedIn uses custom OAuth flow
        const response = await fetchApi(`/users/auth/linkedin?token=${effectiveToken || ""}`, 'GET');
        if (response.ok) {
          const data = await response.json();
          if (data.redirect_url) {
            window.location.href = data.redirect_url;
          }
        } else {
          throw new Error('Failed to initiate LinkedIn OAuth');
        }
      } else {
        // Google uses Devise OAuth route
        const oauthProvider = provider === 'google' ? 'google_oauth2' : provider;
        window.location.href = `/users/auth/${oauthProvider}`;
      }
    } catch (error) {
      console.error(`${provider} Login Error:`, error);

      Mixpanel.track("User Signup Failed", {
        signup_method: provider,
        tenant_name: tenant_name,
        error: "Network error",
      });

      setNotification({
        type: "failure",
        content: `Network error with ${provider} login`,
      });
      setLoading(false);
    }
  };

  const handleSubmit = async (values) => {
    setNotification(null);
    setLoading(true);

    Mixpanel.track("User Signup Attempted", {
      signup_method: "email",
      tenant_name: tenant_name,
    });

    try {
      const response = await fetchApi(
        `/users?redirect_to=${encodeURIComponent("/onboarding")}`,
        "POST",
        {
          user: { ...values },
          token: effectiveToken,
        }
      );

      if (response.ok) {
        await response.json();
        form.reset();

        Mixpanel.track("User Signup Success", {
          signup_method: "email",
          tenant_name: tenant_name,
          user_email: values.email,
        });

        setNotification({
          type: "success",
          content: "Signup successful. Please check your email to confirm.",
        });
      } else {
        const errorData = await response.json();

        Mixpanel.track("User Signup Failed", {
          signup_method: "email",
          tenant_name: tenant_name,
          error: errorData.error || "Signup failed",
        });

        setNotification({
          type: "failure",
          content: errorData.error || "Signup failed",
        });
      }
    } catch (error) {
      console.error("Signup Error:", error);

      Mixpanel.track("User Signup Failed", {
        signup_method: "email",
        tenant_name: tenant_name,
        error: "Network error",
      });

      setNotification({
        type: "failure",
        content: "Network error. Please try again.",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <MantineProvider
      theme={{
        fontSizes: {
          xs: "12px",
          sm: "14px",
          md: "16px",
          lg: "18px",
          xl: "20px",
        },
        colors: {
          brand: [
            "#e3f2fd",
            "#bbdefb",
            "#90caf9",
            "#64b5f6",
            "#42a5f5",
            "#2196f3",
            "#1e88e5",
            "#1976d2",
            "#1565c0",
            "#0d47a1",
          ],
        },
        primaryColor: "brand",
        components: {
          TextInput: {
            defaultProps: {
              size: "md",
              radius: "md",
            },
            styles: (theme) => ({
              input: {
                height: "44px",
                fontSize: "14px",
                border: `1px solid ${theme.colors.gray[3]}`,
                "&:focus": {
                  borderColor: theme.colors.brand[5],
                  boxShadow: `0 0 0 2px ${theme.colors.brand[1]}`,
                },
              },
              label: {
                fontSize: "14px",
                fontWeight: 500,
                color: theme.colors.gray[7],
                marginBottom: "6px",
              },
            }),
          },
          PasswordInput: {
            defaultProps: {
              size: "md",
              radius: "md",
            },
            styles: (theme) => ({
              input: {
                height: "44px",
                fontSize: "14px",
                border: `1px solid ${theme.colors.gray[3]}`,
                "&:focus": {
                  borderColor: theme.colors.brand[5],
                  boxShadow: `0 0 0 2px ${theme.colors.brand[1]}`,
                },
              },
              label: {
                fontSize: "14px",
                fontWeight: 500,
                color: theme.colors.gray[7],
                marginBottom: "6px",
              },
            }),
          },
          Button: {
            styles: (theme) => ({
              root: {
                height: "44px",
                fontSize: "14px",
                fontWeight: 600,
                borderRadius: theme.radius.md,
                transition: "all 0.2s ease",
                "&:hover": {
                  transform: "translateY(-1px)",
                  boxShadow: theme.shadows.md,
                },
              },
            }),
          },
        },
      }}
    >
      <div className="flex justify-center">
        <button
          className="group relative bg-gradient-to-r from-blue-600 to-blue-700 text-white font-semibold px-8 py-3 rounded-xl shadow-lg hover:from-blue-700 hover:to-blue-800 transition-all duration-300 transform hover:scale-105 hover:shadow-xl flex items-center gap-2"
          onClick={() => setOpened(true)}
        >
          <IconUser size={18} />
          Sign Up
          <IconArrowRight
            size={16}
            className="transition-transform duration-300 group-hover:translate-x-1"
          />
        </button>
      </div>

      <Modal
        opened={opened}
        onClose={() => setOpened(false)}
        centered
        size="auto"
        overlayProps={{ blur: 4, opacity: 0.35 }}
        radius="lg"
        padding={0}
        withCloseButton={false}
        styles={{
          modal: {
            padding: 0,
            width: "min(420px, calc(100vw - 48px))",
          },
        }}
      >
        <Paper p={32} radius="lg" style={{ width: "100%" }}>
            <Box mb={24}>
              <Text size="xl" fw={700} ta="center" c="dark.8" mb={4}>
                Create your account
              </Text>
              <Text size="sm" c="dimmed" ta="center">
                {tenant_name
                  ? `Join ${tenant_name} on Nowcollab`
                  : "Get started with Nowcollab"}
              </Text>
            </Box>

            {notification && (
              <Box mb={20}>
                <Notification {...notification} />
              </Box>
            )}

            <form onSubmit={form.onSubmit(handleSubmit)}>
              <Stack gap={16}>
                <TextInput
                  label="Email"
                  placeholder="Enter your email"
                  required
                  icon={<IconMail size={18} />}
                  {...form.getInputProps("email")}
                />


                <Group grow gap={12}>
                  <TextInput
                    label="First name"
                    placeholder="Enter first name"
                    required
                    icon={<IconUser size={18} />}
                    {...form.getInputProps("first_name")}
                  />
                  <TextInput
                    label="Last name"
                    placeholder="Enter last name"
                    required
                    icon={<IconUser size={18} />}
                    {...form.getInputProps("last_name")}
                  />
                </Group>

                
                <Group grow gap={12}>
                  <PasswordInput
                    label="Password"
                    placeholder="Create a password"
                    required
                    icon={<IconLock size={18} />}
                    {...form.getInputProps("password")}
                  />
                  <PasswordInput
                    label="Confirm password"
                    placeholder="Re-enter your password"
                    required
                    icon={<IconLock size={18} />}
                    {...form.getInputProps("password_confirmation")}
                  />
                </Group>

                <Button
                  fullWidth
                  mt={8}
                  type="submit"
                  loading={loading}
                  size="md"
                  gradient={{ from: "brand.6", to: "brand.7", deg: 135 }}
                  styles={(theme) => ({
                    root: {
                      height: "48px",
                      fontSize: "15px",
                      fontWeight: 600,
                      boxShadow: theme.shadows.sm,
                    },
                  })}
                >
                  Sign Up
                </Button>

                <Divider
                  label="Or continue with"
                  labelPosition="center"
                  my={4}
                  styles={(theme) => ({
                    label: {
                      fontSize: "13px",
                      color: theme.colors.gray[5],
                      fontWeight: 500,
                    },
                  })}
                />

                <Group grow gap={12}>
                  <GoogleButton
                    radius="md"
                    size="md"
                    onClick={() => handleSocialLogin("google")}
                    styles={{
                      root: {
                        height: "44px",
                        fontSize: "14px",
                        border: "1px solid #e5e7eb",
                        transition: "all 0.2s ease",
                        "&:hover": {
                          transform: "translateY(-1px)",
                          boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
                        },
                      },
                    }}
                  >
                    Google
                  </GoogleButton>
                  <LinkedInButton
                    radius="md"
                    size="md"
                    onClick={() => handleSocialLogin("linkedin")}
                    styles={{
                      root: {
                        height: "44px",
                        fontSize: "14px",
                        border: "1px solid #e5e7eb",
                        transition: "all 0.2s ease",
                        "&:hover": {
                          transform: "translateY(-1px)",
                          boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
                        },
                      },
                    }}
                  >
                    LinkedIn
                  </LinkedInButton>
                </Group>
              </Stack>
            </form>
          </Paper>
      </Modal>
    </MantineProvider>
  );
}